import is from '@sindresorhus/is';
import { useCallback } from 'react';

import type { CustomJobProfileTemplateRequest } from '../../../../../data-contract/job-profile-template';
import { useCustomTemplate } from './use-custom-template';
import { useManagedTemplate } from './use-managed-template';
import { useSaveTemplate } from './use-save-template';

type ActivateManagedTemplate = {
    readonly activate: () => Promise<boolean>;
    readonly isPending: boolean;
};

export const useActivateManagedTemplate = (
    jobArchitectureId: string,
    enabled: boolean = true,
): ActivateManagedTemplate => {
    const managedTemplateQuery = useManagedTemplate(enabled);
    const customTemplateQuery = useCustomTemplate(jobArchitectureId, enabled);
    const { mutateAsync, isPending } = useSaveTemplate();

    const activate = useCallback(async (): Promise<boolean> => {
        const managedTemplate = managedTemplateQuery.data;
        if (is.nullOrUndefined(managedTemplate)) {
            return false;
        }

        const customTemplate = customTemplateQuery.data ?? null;
        const templateId = customTemplate?.id ?? crypto.randomUUID();
        const payload: CustomJobProfileTemplateRequest = {
            description: managedTemplate.description,
            jobLevelSections: managedTemplate.jobLevelSections,
            jobRoleSections: managedTemplate.jobRoleSections,
            levelSkillLimits: managedTemplate.levelSkillLimits,
            name: managedTemplate.name,
            roleSkillLimits: managedTemplate.roleSkillLimits,
        };

        await mutateAsync({ jobArchitectureId, payload, templateId });
        return true;
    }, [jobArchitectureId, managedTemplateQuery.data, customTemplateQuery.data, mutateAsync]);

    return { activate, isPending };
};
